import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { SettingsComponent } from '../shared/settings/settings.component';
import { CollapsibleCardComponent } from '../components/collapsible-card/collapsible-card.component';
import { ProfileDetailsComponent } from '../components/profile-details/profile-details.component';
import { PasswordSettingsComponent } from '../components/password-settings/password-settings.component';
import { PlanDetailsComponent } from '../components/plan-details/plan-details.component';
import { BusinessProfileComponent } from '../components/business-profile/business-profile.component';
import { LocationManagementComponent } from '../components/location-management/location-management.component';
import { AddressFormComponent } from '../components/address-form/address-form.component';
import { ContactDetailsComponent } from '../components/contact-details/contact-details.component';
import { OperatingHoursComponent } from '../components/operating-hours/operating-hours.component';
import { LocationDetailsModalComponent } from '../components/location-details-modal/location-details-modal.component';
import { DeleteLocationModalComponent } from '../components/delete-location-modal/delete-location-modal.component';
import { EmptyLocationComponent } from '../components/empty-location/empty-location.component';
import { TeamMembersComponent } from '../components/team-members/team-members.component';
import { PaymentSettingsComponent } from '../components/payment-settings/payment-settings.component';
import { TerminalSettingsComponent } from '../components/terminal-settings/terminal-settings.component';
import { AuthService } from '../services/auth.service';
import { ApiService } from '../services/api.service';
import { ProfileService } from '../services/profile.service';
import { LocationService } from '../services/location.service';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  created_at: string;
  picture_url: string;
  contact_number: string;
  role: string;
  emp_id: string;
}

interface Location {
  id: string;
  branch: string;
  active?: boolean;
  [key: string]: any;
}

/**
 * Manages the user profile UI, account & business settings, and location management (list, select, edit, delete).
 * @component
 * @selector app-profile
 * @example
 *   <app-profile></app-profile>
 * @input {none} none - This component does not expose @Input properties; it loads data via services and reads route query params for tab selection.
 * @output {none} none - This component does not emit @Output events; it communicates through injected services and local callbacks.
 *
 * Purpose:
 * - Coordinates profile-related child components (personal details, password, business profile, address, contact details, operating hours, etc.).
 * - Loads and refreshes the user profile and locations using ApiService and ProfileService.
 * - Controls UI state such as activeTab, openCard, modal visibility, and selected location details.
 *
 * Key methods:
 * - refreshProfileData(): Fetches the current user profile from the API and updates the local userProfile state.
 * - onLocationSelected(locationId: string): Requests and sets detailed data for a single location.
 * - onLocationUpdated(updatedLocation: any): Updates the local selectedLocationDetails when a location is changed.
 * - openModal()/closeModal(): Open and close the general location modal.
 * - openDeleteModal()/closeDeleteModal(): Manage delete confirmation modal and selectedLocation for deletion.
 * - onDeleteConfirm(location: Location): Calls the service to delete a location and clears selection on success.
 * - toggleCard(cardTitle: string): Toggles which collapsible card is open in the UI.
 * - onTabChange(tabName: string): Switches the active tab and adjusts the default open card; refreshes profile when switching to User Profile.
 * - onEditProfile(cardTitle: string)/onCancelProfileEdit(cardTitle: string): Proxy edit/cancel actions to child components and restore UI/edit state; refresh profile data when personal details are canceled/updated.
 * - onLocationSaved(response: any): Placeholder for post-save logic; locations list is maintained by the service subscription.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Initializes component by refreshing profile data, subscribing to route query params for tab selection, and conditionally loading & subscribing to location updates from ProfileService. Handles auto-selection of the first location, preserves selection when possible, and clears selection when locations are removed.
 */
@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    SettingsComponent,
    CollapsibleCardComponent,
    ProfileDetailsComponent,
    PasswordSettingsComponent,
    PlanDetailsComponent,
    BusinessProfileComponent,
    LocationManagementComponent,
    AddressFormComponent,
    ContactDetailsComponent,
    OperatingHoursComponent,
    LocationDetailsModalComponent,
    DeleteLocationModalComponent,
    EmptyLocationComponent,
    TeamMembersComponent,
    PaymentSettingsComponent,
    TerminalSettingsComponent,
  ],
  templateUrl: './profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @ViewChild('profileDetails') profileDetails?: ProfileDetailsComponent;
  @ViewChild('passwordSettings') passwordSettings?: PasswordSettingsComponent;
  @ViewChild('businessProfile') businessProfile?: BusinessProfileComponent;
  @ViewChild('addressForm') addressForm?: AddressFormComponent;
  @ViewChild('contactDetails') contactDetails?: ContactDetailsComponent;
  @ViewChild('operatingHours') operatingHours?: OperatingHoursComponent;
  @ViewChild('personalDetailsCard') personalDetailsCard?: CollapsibleCardComponent;
  @ViewChild('passwordCard') passwordCard?: CollapsibleCardComponent;
  @ViewChild('businessDetailsCard')
  businessDetailsCard?: CollapsibleCardComponent;
  @ViewChild('addressCard') addressCard?: CollapsibleCardComponent;
  @ViewChild('contactDetailsCard')
  contactDetailsCard?: CollapsibleCardComponent;
  @ViewChild('operatingHoursCard')
  operatingHoursCard?: CollapsibleCardComponent;

  title = 'web-carwash';
  openCard = 'Personal Details';
  activeTab = 'User Profile';
  isModalOpen = false;
  isDeleteModalOpen = false;
  userProfile: UserProfile | null = null;
  locations: Location[] = [];
  selectedLocationDetails: any = null;
  selectedLocation: Location | null = null;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private profileService: ProfileService,
    private locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  /**
   * Initialize profile component: refresh profile data, apply tab from query params, load and synchronize locations and selection based on user role.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} Performs initialization tasks and subscribes to route and profile services; does not return a value.
   **/
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.syncShopLocationForCurrentRoute();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncShopLocationForCurrentRoute());

    // Subscribe to central profile service to avoid duplicate API calls
    this.profileService.userProfile$.pipe(takeUntil(this.destroy$)).subscribe(profile => {
      if (profile) {
        this.userProfile = profile as UserProfile;
      }
    });

    // Load profile if not already loaded by Header/SideNav
    this.profileService.loadUserProfileIfNeeded();
    // Handle query parameters for tab selection
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const tab = params['tab'];
      if (tab) {
        this.activeTab = tab;
        this.onTabChange(tab);
      }
    });

    // Load locations using the stateful service only if role is not staff
    const userRole = this.authService.user()?.role?.toLowerCase();
    if (userRole !== 'attendant' && userRole !== 'attendant_plus' && userRole !== 'staff') {
      this.profileService.loadLocations();

      // Subscribe to locations updates
      this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
        const previousLocationsCount = this.locations.length;
        const previousSelectedId = this.selectedLocationDetails?.id;
        this.locations = locations;

        // If locations were deleted and we had a selection, check if it still exists
        if (previousSelectedId && locations.length < previousLocationsCount) {
          const selectedLocationStillExists = locations.some(loc => loc.id === previousSelectedId);
          if (!selectedLocationStillExists) {
            // Selected location was deleted, clear selection
            this.selectedLocationDetails = null;
          }
        }

        // Auto-select first location if no location is selected and locations exist
        if (this.locations.length > 0 && !this.selectedLocationDetails) {
          this.locations[0].active = true;
          this.onLocationSelected(this.locations[0].id);
        } else if (this.locations.length === 0) {
          // No locations left, clear selection
          this.selectedLocationDetails = null;
        }
      });
    }
  }

  private syncShopLocationForCurrentRoute(): void {
    const path = this.router.url.split('?')[0];
    if (path === '/profile' || path.startsWith('/settings')) {
      this.locationService.clearShopLocation();
    }
  }

  /**
   * Refresh the user's profile data by requesting it from the API and updating this.userProfile.
   * @example
   * refreshProfileData()
   * void
   * @param {void} none - No arguments.
   * @returns {void} No return value; updates the component's userProfile or logs an error on failure.
   **/
  private refreshProfileData(): void {
    this.profileService.refreshUserProfile();
  }

  onLocationSelected(locationId: string): void {
    this.profileService.getLocationDetails(locationId).subscribe(response => {
      if (response.status && response.data) {
        this.selectedLocationDetails = response.data;
      }
    });
  }

  onLocationUpdated(updatedLocation: any): void {
    // Update selectedLocationDetails with fresh data from API
    this.selectedLocationDetails = updatedLocation;
  }

  openModal() {
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  toggleCard(cardTitle: string) {
    this.openCard = this.openCard === cardTitle ? '' : cardTitle;
  }

  /**
   * Update the currently active tab and set the corresponding open card; refreshes profile data when switching to "User Profile".
   * @example
   * nTabChange('User Profile')
   * undefined
   * @param {{string}} {{tabName}} - Name of the tab to activate (e.g., 'User Profile', 'Account Settings', etc.).
   * @returns {{void}} No return value.
   **/
  onTabChange(tabName: string) {
    this.activeTab = tabName;
    switch (tabName) {
      case 'User Profile':
        this.openCard = 'Personal Details';
        // Profile data is automatically kept in sync via ProfileService subscription
        break;
      case 'Account Settings':
        this.openCard = 'Plan Details';
        break;
      case 'Business Profile':
        this.openCard = 'Business Details';
        break;
      case 'Location Management':
        this.openCard = 'Address';
        break;
      case 'Payment':
        this.openCard = '';
        break;
      case 'Terminal':
        this.openCard = '';
        break;
      case 'Help & Support':
        this.openCard = '';
        break;
      default:
        this.openCard = '';
        break;
    }
  }

  /**
   * Call the appropriate subcomponent's onEdit() based on the provided profile card title.
   * @example
   * nEditProfile('Personal Details')
   * undefined
   * @param {{string}} {{cardTitle}} - The title of the profile card to switch to edit mode for.
   * @returns {{void}} No return value; triggers the edit action on the matching profile section.
   **/
  onEditProfile(cardTitle: string): void {
    switch (cardTitle) {
      case 'Personal Details':
        this.profileDetails?.onEdit();
        break;
      case 'Password':
        this.passwordSettings?.onEdit();
        break;
      case 'Business Details':
        this.businessProfile?.onEdit();
        break;
      case 'Address':
        this.addressForm?.onEdit();
        break;
      case 'Contact Details':
        this.contactDetails?.onEdit();
        break;
      case 'Operating Hours':
        this.operatingHours?.onEdit();
        break;
    }
  }

  /**
   * Cancels edit mode for a specified profile card and restores its edit button; refreshes profile data when cancelling Personal Details.
   * @example
   * nCancelProfileEdit('Personal Details')
   * undefined
   * @param {{string}} {{cardTitle}} - The title of the profile card whose edit mode should be cancelled (e.g., 'Personal Details', 'Password', 'Business Details', 'Address', 'Contact Details', 'Operating Hours').
   * @returns {{void}} Does not return a value.
   **/
  onCancelProfileEdit(cardTitle: string): void {
    if (cardTitle === 'Personal Details' && this.personalDetailsCard) {
      this.personalDetailsCard.showEditButtonAgain();
      // Refresh profile data when personal details are updated
      this.refreshProfileData();
    } else if (cardTitle === 'Password' && this.passwordCard) {
      this.passwordCard.showEditButtonAgain();
    } else if (cardTitle === 'Business Details' && this.businessDetailsCard) {
      this.businessDetailsCard.showEditButtonAgain();
    } else if (cardTitle === 'Address' && this.addressCard) {
      this.addressCard.showEditButtonAgain();
    } else if (cardTitle === 'Contact Details' && this.contactDetailsCard) {
      this.contactDetailsCard.showEditButtonAgain();
    } else if (cardTitle === 'Operating Hours' && this.operatingHoursCard) {
      this.operatingHoursCard.showEditButtonAgain();
    }
  }

  onLocationSaved(response: any) {
    // Handle any additional logic after location is saved
    // The locations list will be automatically updated via the service
  }

  openDeleteModal() {
    if (this.selectedLocationDetails) {
      this.selectedLocation =
        this.locations.find(loc => loc.id === this.selectedLocationDetails.id) || null;
      this.isDeleteModalOpen = true;
    }
  }

  closeDeleteModal() {
    this.isDeleteModalOpen = false;
    this.selectedLocation = null;
  }

  /**
   * Confirm and delete a location by id, close the delete modal and clear selected details if the deleted location was selected.
   * @example
   * nDeleteConfirm({ id: 'loc123' })
   * undefined
   * @param {{Location}} {{location}} - Location object to delete; must contain an id property.
   * @returns {{void}} No return value; performs deletion via service and updates component state (closes modal and clears selectedLocationDetails when applicable).
   **/
  onDeleteConfirm(location: Location) {
    if (location && location.id) {
      this.profileService.deleteLocation(location.id).subscribe({
        next: () => {
          this.closeDeleteModal();
          // Clear selected location details if this was the selected location
          // The locations array will be automatically updated via the subscription in ngOnInit
          // The subscription will handle selecting a new location if available
          if (this.selectedLocationDetails && this.selectedLocationDetails.id === location.id) {
            this.selectedLocationDetails = null;
          }
        },
        error: error => {
          console.error('Failed to delete location:', error);
          // Error handling is done in the service
        },
      });
    }
  }
}
